/**
 * Erika Morón — main product section behaviour.
 *
 *  <em-product-page>     variant selection: fetches the section for the chosen options, swaps variant-dependent
 *                        parts ([data-em-rerender]), syncs the URL and the gallery.
 *  <em-product-gallery>  scroll-snap media carousel: markers, counter, follow-pointer arrow, zoom lightbox.
 *  <em-product-form>     AJAX add to cart that announces itself with Shopify's standard cart events, so Horizon's
 *                        cart drawer, cart bubble and cart items refresh (and the drawer opens).
 *  <em-quantity>         +/- stepper.
 *  <em-sticky-buy>       floating add-to-cart bar.
 *  <em-sticky-column>    sticky information column that still scrolls when taller than the viewport.
 *  <em-countdown>, <em-complementary>
 */

const prefersMotion = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const hasFinePointer = () => window.matchMedia('(pointer: fine)').matches;
const shopRoot = () => window.Shopify?.routes?.root || '/';

let standardEvents;
const getStandardEvents = () => (standardEvents ??= import('@shopify/events').catch(() => null));

/* ------------------------------------------------------------------------------------------------ Product page */

class EmProductPage extends HTMLElement {
  #abort;
  #requestId = 0;
  #variant;
  static #htmlCache = new Map();

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('pointerover', this.#onPointerOver, { signal, passive: true });
    this.addEventListener('touchstart', this.#onPointerOver, { signal, passive: true });

    this.querySelectorAll('[data-em-share]').forEach((button) => {
      if (!navigator.share) return;
      button.hidden = false;
      button.nextElementSibling?.setAttribute('hidden', '');
    });

    this.#rememberView();
    this.#loadModelFeatures();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  get sectionId() {
    return this.dataset.sectionId;
  }

  get formId() {
    return this.dataset.formId;
  }

  get variant() {
    if (this.#variant === undefined) {
      const script = this.querySelector('[data-em-variant]');
      this.#variant = script ? JSON.parse(script.textContent || 'null') : null;
    }
    return this.#variant;
  }

  /** Selected option controls ordered by option position; `swap` replaces the one sharing its position. */
  #selectedControls(swap) {
    const byPosition = new Map();
    const selector = `[data-option-position][form="${CSS.escape(this.formId)}"]`;

    for (const control of this.querySelectorAll(selector)) {
      const position = Number(control.dataset.optionPosition);
      if (control instanceof HTMLSelectElement) {
        const option = control.selectedOptions[0];
        if (option) byPosition.set(position, option);
      } else if (control.checked) {
        byPosition.set(position, control);
      }
    }

    if (swap) byPosition.set(Number(swap.dataset.optionPosition), swap);

    return [...byPosition.entries()].sort((a, b) => a[0] - b[0]).map(([, control]) => control);
  }

  #sectionHtml(optionIds, productUrl = this.dataset.productUrl) {
    const key = `${this.sectionId}|${productUrl}|${optionIds}`;
    const cache = EmProductPage.#htmlCache;

    if (!cache.has(key)) {
      const url = `${productUrl}?option_values=${optionIds}&section_id=${encodeURIComponent(this.sectionId)}`;
      const request = fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Section request failed (${response.status})`);
        return response.text();
      });
      request.catch(() => cache.delete(key));
      cache.set(key, request);
      if (cache.size > 60) cache.delete(cache.keys().next().value);
    }

    return cache.get(key);
  }

  #onPointerOver = (event) => {
    const label = event.target instanceof Element ? event.target.closest('label[for]') : null;
    if (!label) return;
    const input = document.getElementById(label.htmlFor);
    if (!(input instanceof HTMLInputElement) || !input.matches('[data-option-position]') || input.checked) return;

    const controls = this.#selectedControls(input);
    if (controls.some((control) => control.dataset.productUrl && control.dataset.productUrl !== this.dataset.productUrl)) return;
    this.#sectionHtml(controls.map((control) => control.value).join(',')).catch(() => {});
  };

  #onChange = (event) => {
    const control = event.target;
    if (!(control instanceof HTMLElement) || !control.matches('[data-option-position]')) return;

    const controls = this.#selectedControls();
    const optionIds = controls.map((item) => item.value).join(',');
    const otherProduct = controls.map((item) => item.dataset.productUrl).find((url) => url && url !== this.dataset.productUrl);

    if (otherProduct) {
      // Combined listing: a different product owns this combination, so load its page.
      window.location.assign(`${otherProduct}?option_values=${optionIds}`);
      return;
    }

    this.#applyCombination(optionIds);
  };

  async #applyCombination(optionIds) {
    const requestId = ++this.#requestId;
    this.setAttribute('aria-busy', 'true');

    let html;
    try {
      html = await this.#sectionHtml(optionIds);
    } catch (error) {
      console.warn('[em-product] variant update failed', error);
      this.removeAttribute('aria-busy');
      return;
    }

    if (requestId !== this.#requestId) return;

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const fresh = doc.querySelector(`em-product-page[data-section-id="${CSS.escape(this.sectionId)}"]`);
    if (!fresh) {
      this.removeAttribute('aria-busy');
      return;
    }

    const previous = this.variant;
    const script = fresh.querySelector('[data-em-variant]');
    const variant = script ? JSON.parse(script.textContent || 'null') : null;

    this.#swapParts(fresh);
    this.querySelector('em-product-gallery')?.syncWith(fresh.querySelector('em-product-gallery'), variant, previous);
    this.#variant = variant;

    if (variant?.id && this.hasAttribute('data-update-url')) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState(window.history.state, '', url.toString());
    }

    this.dispatchEvent(new CustomEvent('em:variant-change', { bubbles: true, detail: { variant, previousVariant: previous } }));
    window.Shopify?.PaymentButton?.init?.();
    this.removeAttribute('aria-busy');
  }

  #swapParts(fresh) {
    const focusedId = document.activeElement?.id;

    for (const current of this.querySelectorAll('[data-em-rerender]')) {
      const key = current.getAttribute('data-em-rerender');
      const replacement = fresh.querySelector(`[data-em-rerender="${CSS.escape(key)}"]`);
      if (!replacement || !current.isConnected) continue;

      if (current.dataset.emRerenderMode === 'value') {
        // Payment terms web component reacts to its variant input; replacing it would reload the widget.
        const input = current.querySelector('input[name="id"]');
        const nextInput = replacement.querySelector('input[name="id"]');
        if (input && nextInput && input.value !== nextInput.value) {
          input.value = nextInput.value;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
        continue;
      }

      const quantity = current.querySelector('input[name="quantity"]')?.value;
      current.replaceWith(replacement);

      if (quantity) {
        const quantityInput = replacement.querySelector('input[name="quantity"]');
        if (quantityInput) {
          quantityInput.value = quantity;
          quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }
    }

    if (focusedId) document.getElementById(focusedId)?.focus({ preventScroll: true });
  }

  #rememberView() {
    try {
      const key = 'em:recently-viewed';
      const id = Number(this.dataset.productId);
      const list = JSON.parse(localStorage.getItem(key) || '[]').filter((item) => item !== id);
      list.unshift(id);
      localStorage.setItem(key, JSON.stringify(list.slice(0, 20)));
    } catch (error) {
      /* storage unavailable */
    }
  }

  #loadModelFeatures() {
    if (!this.querySelector('model-viewer') || !window.Shopify?.loadFeatures) return;
    const models = JSON.parse(this.querySelector('[data-em-models]')?.textContent || '[]');

    window.Shopify.loadFeatures([
      {
        name: 'shopify-xr',
        version: '1.0',
        onLoad: () => {
          window.ShopifyXR?.addModels?.(models);
          window.ShopifyXR?.setupXRElements?.();
        },
      },
      {
        name: 'model-viewer-ui',
        version: '1.0',
        onLoad: (error) => {
          if (error || !window.Shopify?.ModelViewerUI) return;
          this.querySelectorAll('model-viewer').forEach((viewer) => new window.Shopify.ModelViewerUI(viewer));
        },
      },
    ]);
  }
}

/* ------------------------------------------------------------------------------------------------ Gallery */

class EmLightbox {
  #dialog;
  #track;
  #prev;
  #next;
  #zoomed = null;
  #maxZoom;
  #drag = null;

  constructor(template, maxZoom) {
    this.#maxZoom = Math.max(1, maxZoom || 3);

    const dialog = document.createElement('dialog');
    dialog.className = 'em-lightbox em-scheme em-scheme-1';
    dialog.innerHTML = '<div class="em-lightbox__track"></div>';
    if (template) dialog.append(template.content.cloneNode(true));
    document.body.append(dialog);

    this.#dialog = dialog;
    this.#track = dialog.querySelector('.em-lightbox__track');
    this.#prev = dialog.querySelector('[data-em-lb-step="-1"]');
    this.#next = dialog.querySelector('[data-em-lb-step="1"]');

    dialog.addEventListener('click', this.#onClick);
    dialog.addEventListener('keydown', this.#onKeydown);
    dialog.addEventListener('close', () => this.#unzoom());
    dialog.addEventListener('pointermove', this.#onPointerMove);
    dialog.addEventListener('pointerdown', this.#onPointerDown);
    dialog.addEventListener('pointerup', () => (this.#drag = null));
    this.#track.addEventListener('scroll', () => this.#updateSteps(), { passive: true });
  }

  get #current() {
    return Math.round(this.#track.scrollLeft / Math.max(1, this.#track.clientWidth));
  }

  open(images, startIndex = 0) {
    this.#unzoom();
    this.#track.replaceChildren(
      ...images.map((source) => {
        const slide = document.createElement('div');
        slide.className = 'em-lightbox__slide';
        const img = document.createElement('img');
        img.className = 'em-lightbox__img';
        img.alt = source.alt;
        img.src = source.currentSrc || source.src;
        if (source.srcset) img.srcset = source.srcset;
        img.sizes = '100vw';
        img.draggable = false;
        if (source.getAttribute('width')) img.width = Number(source.getAttribute('width'));
        if (source.getAttribute('height')) img.height = Number(source.getAttribute('height'));
        slide.append(img);
        return slide;
      })
    );

    const single = images.length < 2;
    if (this.#prev) this.#prev.hidden = single;
    if (this.#next) this.#next.hidden = single;

    this.#dialog.showModal();
    requestAnimationFrame(() => {
      this.#track.scrollTo({ left: startIndex * this.#track.clientWidth, behavior: 'instant' });
      this.#updateSteps();
    });
  }

  #go(delta) {
    this.#unzoom();
    const count = this.#track.children.length;
    const target = Math.min(Math.max(this.#current + delta, 0), count - 1);
    this.#track.scrollTo({ left: target * this.#track.clientWidth, behavior: prefersMotion() ? 'smooth' : 'instant' });
  }

  #updateSteps() {
    const count = this.#track.children.length;
    if (this.#prev) this.#prev.disabled = this.#current <= 0;
    if (this.#next) this.#next.disabled = this.#current >= count - 1;
  }

  #onKeydown = (event) => {
    if (event.key === 'ArrowLeft') this.#go(-1);
    if (event.key === 'ArrowRight') this.#go(1);
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-em-lb-close]')) {
      this.#dialog.close();
      return;
    }

    const step = target.closest('[data-em-lb-step]');
    if (step) {
      this.#go(Number(step.getAttribute('data-em-lb-step')));
      return;
    }

    const img = target.closest('.em-lightbox__img');
    if (!img) return;
    if (this.#drag?.moved) return;

    if (this.#zoomed === img) {
      this.#unzoom();
    } else if (this.#maxZoom > 1) {
      this.#zoomAt(img, event.clientX, event.clientY);
    }
  };

  #originFor(img, clientX, clientY) {
    const box = img.parentElement.getBoundingClientRect();
    const x = Math.min(Math.max((clientX - box.left) / box.width, 0), 1) * 100;
    const y = Math.min(Math.max((clientY - box.top) / box.height, 0), 1) * 100;
    return `${x}% ${y}%`;
  }

  #zoomAt(img, clientX, clientY) {
    this.#unzoom();
    img.sizes = `${Math.round(this.#maxZoom * 100)}vw`;
    img.style.transformOrigin = this.#originFor(img, clientX, clientY);
    img.style.transform = `scale(${this.#maxZoom})`;
    img.classList.add('is-zoomed');
    this.#dialog.classList.add('is-zoomed');
    this.#zoomed = img;
  }

  #unzoom() {
    if (!this.#zoomed) return;
    this.#zoomed.style.transform = '';
    this.#zoomed.classList.remove('is-zoomed');
    this.#dialog.classList.remove('is-zoomed');
    this.#zoomed = null;
  }

  #onPointerDown = (event) => {
    this.#drag = { x: event.clientX, y: event.clientY, moved: false };
  };

  #onPointerMove = (event) => {
    if (this.#drag && Math.hypot(event.clientX - this.#drag.x, event.clientY - this.#drag.y) > 6) this.#drag.moved = true;
    if (!this.#zoomed) return;
    if (event.pointerType !== 'mouse' && !this.#drag) return;

    if (event.pointerType === 'mouse') {
      this.#zoomed.style.transformOrigin = this.#originFor(this.#zoomed, event.clientX, event.clientY);
    } else {
      // Touch: dragging moves the view in the opposite direction of the finger, like panning a photo.
      const box = this.#zoomed.parentElement.getBoundingClientRect();
      const mirroredX = box.left + box.right - event.clientX;
      const mirroredY = box.top + box.bottom - event.clientY;
      this.#zoomed.style.transformOrigin = this.#originFor(this.#zoomed, mirroredX, mirroredY);
    }
  };
}

class EmProductGallery extends HTMLElement {
  #abort;
  #resizeObserver;
  #index = 0;
  #programmatic = false;
  #frame = 0;
  #endTimer;
  #lightbox;

  connectedCallback() {
    this.track = this.querySelector('[data-em-track]');
    if (!this.track) return;

    this.stage = this.querySelector('.em-gallery__stage');
    this.cursor = this.querySelector('.em-gallery__cursor');
    this.stageZoom = this.querySelector('.em-gallery__zoom--stage');

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#index = Math.max(0, this.cells.findIndex((cell) => cell.hasAttribute('data-initial')));

    this.track.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    this.track.addEventListener('scrollend', () => this.#settle(), { signal });
    this.addEventListener('click', this.#onClick, { signal });

    if (this.cursor && this.stage) {
      this.stage.addEventListener('pointermove', this.#onPointerMove, { passive: true, signal });
      this.stage.addEventListener('pointerleave', () => this.cursor.classList.remove('is-visible'), { signal });
    }

    if (this.hasAttribute('data-zoom-max')) {
      this.track.addEventListener(
        'gesturestart',
        (event) => {
          event.preventDefault();
          const onChange = (change) => {
            change.preventDefault();
            if (change.scale > 1.5) {
              this.track.removeEventListener('gesturechange', onChange);
              this.openLightbox();
            }
          };
          this.track.addEventListener('gesturechange', onChange, { signal });
        },
        { signal }
      );
    }

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.isScrollable && this.#closestIndex() !== this.#index) this.select(this.#index, true);
      this.#adaptHeight();
    });
    this.#resizeObserver.observe(this.track);

    requestAnimationFrame(() => {
      if (this.#index > 0) this.select(this.#index, true);
      this.#refresh();
      this.#adaptHeight();
    });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#resizeObserver?.disconnect();
    cancelAnimationFrame(this.#frame);
  }

  get cells() {
    return [...this.track.querySelectorAll(':scope > [data-media-id]')].filter((cell) => !cell.hidden);
  }

  get isScrollable() {
    return this.track.scrollWidth - this.track.clientWidth > 1;
  }

  get currentCell() {
    return this.cells[this.#index];
  }

  select(index, instant = false) {
    const cell = this.cells[index];
    if (!cell) return;

    this.#index = index;
    const left = this.#scrollLeftFor(cell);

    if (Math.abs(this.track.scrollLeft - left) < 1) {
      this.#settle();
      return;
    }

    this.#programmatic = true;
    this.track.scrollTo({ left, behavior: instant || !prefersMotion() ? 'instant' : 'smooth' });
    this.#refresh();
  }

  #scrollLeftFor(cell) {
    const max = this.track.scrollWidth - this.track.clientWidth;
    const centered = cell.offsetLeft - (this.track.clientWidth - cell.offsetWidth) / 2;
    return Math.min(Math.max(centered, 0), max);
  }

  #closestIndex() {
    const middle = this.track.scrollLeft + this.track.clientWidth / 2;
    let best = 0;
    let bestDistance = Infinity;
    this.cells.forEach((cell, index) => {
      const distance = Math.abs(cell.offsetLeft + cell.offsetWidth / 2 - middle);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = index;
      }
    });
    return best;
  }

  #onScroll = () => {
    if (!this.#frame) {
      this.#frame = requestAnimationFrame(() => {
        this.#frame = 0;
        if (this.#programmatic) return;
        const index = this.#closestIndex();
        if (index !== this.#index) {
          this.#index = index;
          this.#refresh();
        }
      });
    }

    if (!('onscrollend' in window)) {
      clearTimeout(this.#endTimer);
      this.#endTimer = setTimeout(() => this.#settle(), 90);
    }
  };

  #settle() {
    this.#programmatic = false;
    if (this.isScrollable) this.#index = this.#closestIndex();
    this.#refresh();
    this.#adaptHeight();

    const cell = this.currentCell;
    if (this.hasAttribute('data-autoplay') && cell) {
      if (cell.dataset.mediaType === 'video') cell.querySelector('video')?.play().catch(() => {});
      if (cell.dataset.mediaType === 'external_video') this.#loadExternal(cell);
    }
  }

  #adaptHeight() {
    const cell = this.currentCell;
    if (!cell) return;
    this.track.style.maxHeight = this.isScrollable ? `${cell.offsetHeight}px` : '';
  }

  #refresh() {
    const cells = this.cells;
    const cell = cells[this.#index];

    this.querySelectorAll('[data-em-nav]').forEach((nav) => {
      const buttons = [...nav.querySelectorAll('[data-em-go]')].filter((button) => !button.hidden);
      buttons.forEach((button, index) => button.setAttribute('aria-current', String(index === this.#index)));

      const active = buttons[this.#index];
      if (active && nav.scrollWidth > nav.clientWidth) {
        nav.scrollTo({
          left: active.offsetLeft - nav.clientWidth / 2 + active.offsetWidth / 2,
          behavior: prefersMotion() ? 'smooth' : 'instant',
        });
      }
    });

    this.querySelectorAll('[data-em-counter-current]').forEach((el) => (el.textContent = String(this.#index + 1)));
    this.querySelectorAll('[data-em-counter-total]').forEach((el) => (el.textContent = String(cells.length)));

    const isImage = cell?.dataset.mediaType === 'image';
    if (this.cursor) this.cursor.hidden = !isImage;
    if (this.stageZoom) this.stageZoom.hidden = !isImage;

    this.track.querySelectorAll(':scope > [data-media-id]').forEach((item) => {
      if (item !== cell) this.#pauseMedia(item);
    });

    [cells[this.#index - 1], cell, cells[this.#index + 1]].forEach((item) => {
      item?.querySelectorAll('img[loading="lazy"]').forEach((img) => (img.loading = 'eager'));
    });
  }

  #pauseMedia(cell) {
    cell.querySelector('video')?.pause();
    const iframe = cell.querySelector('iframe');
    if (!iframe?.contentWindow) return;
    try {
      if (iframe.src.includes('youtube')) {
        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: '' }), '*');
      } else if (iframe.src.includes('vimeo')) {
        iframe.contentWindow.postMessage(JSON.stringify({ method: 'pause' }), '*');
      }
    } catch (error) {
      /* cross-origin player not ready */
    }
  }

  #loadExternal(cell) {
    const holder = cell.querySelector('.em-gallery__deferred');
    const template = holder?.querySelector('template');
    if (!holder || !template) return;
    holder.querySelector('[data-em-play]')?.remove();
    holder.append(template.content.cloneNode(true));
    template.remove();
  }

  #onPointerMove = (event) => {
    if (event.pointerType !== 'mouse' || !this.cursor) return;
    const overControl = event.target instanceof Element && event.target.closest('button, a[href], [data-em-nav]');

    if (overControl || this.cursor.hidden || !this.isScrollable) {
      this.cursor.classList.remove('is-visible');
      return;
    }

    const box = this.stage.getBoundingClientRect();
    const x = event.clientX - box.left;
    const y = event.clientY - box.top;
    this.cursor.classList.add('is-visible');
    this.cursor.classList.toggle('is-flipped', x < box.width / 2);
    this.cursor.style.translate = `${(x - this.cursor.offsetWidth / 2).toFixed(1)}px ${(y - this.cursor.offsetHeight / 2).toFixed(1)}px`;
  };

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const go = target.closest('[data-em-go]');
    if (go) {
      const buttons = [...go.parentElement.querySelectorAll('[data-em-go]')].filter((button) => !button.hidden);
      this.select(buttons.indexOf(go));
      return;
    }

    const step = target.closest('[data-em-step]');
    if (step) {
      const next = Math.min(Math.max(this.#index + Number(step.getAttribute('data-em-step')), 0), this.cells.length - 1);
      this.select(next);
      return;
    }

    const play = target.closest('[data-em-play]');
    if (play) {
      this.#loadExternal(play.closest('[data-media-id]'));
      return;
    }

    const zoom = target.closest('[data-em-zoom]');
    if (zoom) {
      const ownCell = zoom.closest('[data-media-id]');
      const images = this.cells.filter((cell) => cell.dataset.mediaType === 'image');
      this.openLightbox(ownCell ? images.indexOf(ownCell) : undefined);
      return;
    }

    // Desktop carousel: clicking the left or right half of the picture moves backwards or forwards.
    if (this.cursor && hasFinePointer() && this.isScrollable && this.track.contains(target) && !target.closest('button, a[href], video, iframe, model-viewer')) {
      if (this.currentCell?.dataset.mediaType !== 'image') return;
      const box = this.stage.getBoundingClientRect();
      const forward = event.clientX - box.left > box.width / 2;
      this.select(Math.min(Math.max(this.#index + (forward ? 1 : -1), 0), this.cells.length - 1));
    }
  };

  openLightbox(index) {
    if (!this.hasAttribute('data-zoom-max')) return;
    const imageCells = this.cells.filter((cell) => cell.dataset.mediaType === 'image');
    const images = imageCells.map((cell) => cell.querySelector('img')).filter(Boolean);
    if (!images.length) return;

    const start = index ?? Math.max(0, imageCells.indexOf(this.currentCell));
    this.#lightbox ??= new EmLightbox(this.querySelector('template[data-em-lightbox-ui]'), Number(this.dataset.zoomMax));
    this.#lightbox.open(images, Math.max(0, start));
  }

  /** Called after a variant change with the freshly rendered gallery. */
  syncWith(freshGallery, variant, previousVariant) {
    if (freshGallery) {
      freshGallery.querySelectorAll('[data-media-id]').forEach((freshItem) => {
        const id = freshItem.getAttribute('data-media-id');
        this.querySelectorAll(`[data-media-id="${CSS.escape(id)}"]`).forEach((item) => {
          if (item.tagName === freshItem.tagName) item.hidden = freshItem.hidden;
        });
      });
    }

    const mediaId = variant?.featured_media?.id;
    if (mediaId && mediaId !== previousVariant?.featured_media?.id) {
      const cell = this.track.querySelector(`[data-media-id="${mediaId}"]`);
      const index = this.cells.indexOf(cell);
      if (index >= 0) {
        if (this.isScrollable) {
          this.select(index, true);
        } else {
          this.#index = index;
          cell.scrollIntoView({ block: 'start', behavior: prefersMotion() ? 'smooth' : 'instant' });
        }
      }
    }

    this.#index = Math.min(this.#index, Math.max(0, this.cells.length - 1));
    this.#refresh();
    this.#adaptHeight();
  }
}

/* ------------------------------------------------------------------------------------------------ Add to cart */

class EmProductForm extends HTMLElement {
  #abort;
  #busy = false;

  connectedCallback() {
    this.form = this.querySelector('form');
    if (!this.form) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.form.addEventListener('submit', this.#onSubmit, { signal });
    this.addEventListener('change', this.#onRecipientToggle, { signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onRecipientToggle = (event) => {
    const toggle = event.target;
    if (!(toggle instanceof HTMLInputElement) || !toggle.matches('[data-em-recipient-toggle]')) return;

    const fields = this.querySelector('[data-em-recipient-fields]');
    if (!fields) return;
    fields.hidden = !toggle.checked;
    fields.querySelectorAll('input, textarea').forEach((field) => (field.disabled = !toggle.checked));

    const offset = fields.querySelector('[data-em-recipient-offset]');
    if (offset) offset.value = String(new Date().getTimezoneOffset());
  };

  #submitButtons() {
    const outside = document.querySelectorAll(`button[type="submit"][form="${CSS.escape(this.form.id)}"]`);
    return [...this.form.querySelectorAll('button[type="submit"]'), ...outside];
  }

  #setMessage(text) {
    const error = this.querySelector('[data-em-error]');
    if (!error) return;
    error.textContent = text || '';
    error.hidden = !text;
  }

  #announce(text) {
    const live = this.querySelector('[data-em-live]');
    if (!live) return;
    live.textContent = text;
    setTimeout(() => (live.textContent = ''), 4000);
  }

  #onSubmit = async (event) => {
    event.preventDefault();
    if (this.#busy) return;

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const buttons = this.#submitButtons();
    this.#busy = true;
    buttons.forEach((button) => button.setAttribute('aria-busy', 'true'));

    const events = await getStandardEvents();
    const formData = new FormData(this.form);
    const quantity = Number(formData.get('quantity')) || 1;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => element instanceof HTMLElement && element.dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.set('sections', [...new Set(sectionIds)].join(','));

    let deferred = null;
    if (events?.CartLinesUpdateEvent) {
      deferred = events.CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: String(formData.get('id')), quantity }],
          promise: deferred.promise,
        })
      );
    }

    try {
      const response = await fetch(`${shopRoot()}cart/add.js`, {
        method: 'POST',
        body: formData,
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      });
      const result = await response.json();
      const failed = !response.ok || Boolean(result.status);

      if (!deferred) {
        if (failed) this.#setMessage(result.description || result.message);
        else window.location.assign(`${shopRoot()}cart`);
        return;
      }

      const cart = await fetch(`${shopRoot()}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const detail = {
        items: cart.items,
        source: 'em-product-form',
        sourceId: this.form.id,
        itemCount: quantity,
        productId: this.dataset.productId,
        didError: failed,
      };

      if (failed) {
        this.#setMessage(result.description || result.message);
        this.#announce(result.description || result.message || '');
        if (events.CartErrorEvent) {
          this.dispatchEvent(
            new events.CartErrorEvent({
              error: result.message || 'Add to cart failed',
              code: 'INVALID',
              detail: { description: result.description, errors: result.errors },
            })
          );
        }
      } else {
        this.#setMessage('');
        this.#announce(this.dataset.addedLabel || '');
        detail.sections = result.sections;
      }

      deferred.resolve({ cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail });
    } catch (error) {
      deferred?.reject(error);
      if (error?.name !== 'AbortError') {
        console.error('[em-product-form]', error);
        if (events?.CartErrorEvent) {
          this.dispatchEvent(new events.CartErrorEvent({ error: error?.message || 'Network error', code: 'SERVICE_UNAVAILABLE' }));
        }
      }
    } finally {
      this.#busy = false;
      buttons.forEach((button) => button.removeAttribute('aria-busy'));
    }
  };
}

/* ------------------------------------------------------------------------------------------------ Small widgets */

class EmQuantity extends HTMLElement {
  #abort;

  connectedCallback() {
    this.input = this.querySelector('input');
    this.decrease = this.querySelector('[data-em-qty="-1"]');
    this.increase = this.querySelector('[data-em-qty="1"]');
    if (!this.input) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.decrease?.addEventListener('click', () => this.#step(-1), { signal });
    this.increase?.addEventListener('click', () => this.#step(1), { signal });
    this.input.addEventListener('input', () => this.#update(), { signal });
    this.input.addEventListener('focus', () => this.input.select(), { signal });
    this.input.addEventListener(
      'change',
      () => {
        const min = Number(this.input.min) || 1;
        const max = this.input.max ? Number(this.input.max) : Infinity;
        const value = Number(this.input.value);
        if (!Number.isFinite(value) || value < min) this.input.value = String(min);
        if (value > max) this.input.value = String(max);
        this.#update();
      },
      { signal }
    );

    this.#update();
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #step(direction) {
    direction < 0 ? this.input.stepDown() : this.input.stepUp();
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    this.#update();
  }

  #update() {
    const value = Number(this.input.value) || 0;
    const min = Number(this.input.min) || 1;
    const max = this.input.max ? Number(this.input.max) : Infinity;
    if (this.decrease) this.decrease.disabled = value <= min;
    if (this.increase) this.increase.disabled = value >= max;
    this.style.setProperty('--em-qty-chars', `${Math.max(1, this.input.value.length)}ch`);
  }
}

class EmStickyBuy extends HTMLElement {
  #observer;

  connectedCallback() {
    const anchor = document.getElementById(this.dataset.anchorId) || document.getElementById(this.dataset.formId);
    if (!anchor) return;

    const footer = document.querySelector('.shopify-section-group-footer-group, body > footer, footer');
    let anchorPassed = false;
    let footerVisible = false;

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === anchor) anchorPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        else footerVisible = entry.isIntersecting;
      }
      const visible = anchorPassed && !footerVisible;
      this.classList.toggle('is-visible', visible);
      this.inert = !visible;
    });

    this.#observer.observe(anchor);
    if (footer) this.#observer.observe(footer);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
  }
}

class EmStickyColumn extends HTMLElement {
  #resizeObserver;
  #baseTop = null;
  #top = 0;
  #lastScroll = 0;
  #sticky = false;

  connectedCallback() {
    this.#lastScroll = window.scrollY;
    window.addEventListener('scroll', this.#place, { passive: true });
    this.#resizeObserver = new ResizeObserver(() => {
      this.#baseTop = null;
      this.#place();
    });
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.#place);
    this.#resizeObserver?.disconnect();
  }

  #measure() {
    this.style.removeProperty('top');
    const styles = getComputedStyle(this);
    this.#sticky = styles.position === 'sticky';
    this.#baseTop = parseFloat(styles.top) || 0;
    this.#top = this.#baseTop;
  }

  #place = () => {
    if (this.#baseTop === null) this.#measure();
    const scrollY = window.scrollY;
    const delta = scrollY - this.#lastScroll;
    this.#lastScroll = scrollY;
    if (!this.#sticky) return;

    // How far the column sticks out below the viewport (20px breathing room at the bottom).
    const overflow = this.offsetHeight + this.#baseTop + 20 - window.innerHeight;
    if (overflow <= 0) {
      this.style.removeProperty('top');
      return;
    }

    this.#top = Math.min(this.#baseTop, Math.max(this.#baseTop - overflow, this.#top - delta));
    this.style.top = `${Math.round(this.#top)}px`;
  };
}

class EmCountdown extends HTMLElement {
  #timer;

  connectedCallback() {
    const expires = Date.parse(this.dataset.expiresAt || '');
    if (Number.isNaN(expires)) return;

    const tick = () => {
      const seconds = Math.max(0, Math.floor((expires - Date.now()) / 1000));
      const parts = {
        days: Math.floor(seconds / 86400),
        hours: Math.floor((seconds % 86400) / 3600),
        minutes: Math.floor((seconds % 3600) / 60),
        seconds: seconds % 60,
      };
      Object.entries(parts).forEach(([unit, value]) => {
        const el = this.querySelector(`[data-unit="${unit}"]`);
        if (el) el.textContent = String(value).padStart(2, '0');
      });
      if (seconds === 0) {
        clearInterval(this.#timer);
        if (this.dataset.behavior === 'hide') this.closest('[data-em-countdown-wrap]')?.setAttribute('hidden', '');
      }
    };

    tick();
    this.#timer = setInterval(tick, 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }
}

class EmComplementary extends HTMLElement {
  connectedCallback() {
    if (this.dataset.loaded || !this.dataset.url || this.childElementCount) return;
    this.dataset.loaded = 'true';

    fetch(this.dataset.url)
      .then((response) => response.text())
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector(`em-complementary[data-block-id="${CSS.escape(this.dataset.blockId || '')}"]`);
        if (fresh?.childElementCount) {
          this.replaceChildren(...fresh.childNodes);
          this.hidden = false;
        }
      })
      .catch(() => {});
  }
}

/* ------------------------------------------------------------------------------------------------ Dialog openers */

if (!window.emDialogDelegate) {
  window.emDialogDelegate = true;

  document.addEventListener('click', (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const opener = target.closest('[data-em-dialog-open]');
    if (opener) {
      const dialog = document.getElementById(opener.getAttribute('data-em-dialog-open'));
      if (dialog instanceof HTMLDialogElement) {
        event.preventDefault();
        dialog.showModal();
        opener.setAttribute('aria-expanded', 'true');
        dialog.addEventListener('close', () => opener.setAttribute('aria-expanded', 'false'), { once: true });
      }
      return;
    }

    if (target.closest('[data-em-dialog-close]')) {
      target.closest('dialog')?.close();
      return;
    }

    // Click on the backdrop of an em-dialog closes it.
    if (target instanceof HTMLDialogElement && target.classList.contains('em-dialog')) {
      const box = target.getBoundingClientRect();
      const inside = event.clientX >= box.left && event.clientX <= box.right && event.clientY >= box.top && event.clientY <= box.bottom;
      if (!inside) target.close();
    }
  });

  document.addEventListener('click', (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-em-share]') : null;
    if (!button || !navigator.share) return;
    navigator.share({ title: button.dataset.title, url: button.dataset.url }).catch(() => {});
  });
}

/* ------------------------------------------------------------------------------------------------ Registration */

const definitions = [
  ['em-product-page', EmProductPage],
  ['em-product-gallery', EmProductGallery],
  ['em-product-form', EmProductForm],
  ['em-quantity', EmQuantity],
  ['em-sticky-buy', EmStickyBuy],
  ['em-sticky-column', EmStickyColumn],
  ['em-countdown', EmCountdown],
  ['em-complementary', EmComplementary],
];

for (const [name, constructor] of definitions) {
  if (!customElements.get(name)) customElements.define(name, constructor);
}
